import { v1NotificationsDirectoryConnectionToken } from '@repo/sdk/gen'
import {
	Centrifuge,
	connectingCodes,
	type RpcResult,
	type SubscribedContext,
	subscribingCodes,
	UnauthorizedError
} from 'centrifuge'
import { type DirectorySignal, toDirectorySignal } from './directory-signal'

/**
 * What the view is allowed to know about the feed, and nothing more.
 *
 * Four states rather than Centrifugo's own, because the question a person is
 * asking is not "is the websocket open" — it is "is this list still true".
 * `live` answers yes. The other three answer no, and differ only in whether
 * waiting will fix it, which is the one thing that changes what we should tell
 * them.
 *
 * There is deliberately no `error`. A feed that is down is a feed that is
 * down; why it is down belongs in Sentry, not in a sentence somebody reads
 * while trying to find a file.
 */
export type DirectoryFeedState =
	/** Opening for the first time. Nothing to report yet — the page was just drawn from the server, so it is as fresh as it will ever be. */
	| 'connecting'
	/** Subscribed. Events are arriving. */
	| 'live'
	/** Not receiving, still trying. Centrifugo backs off on its own; there is nothing for anyone to do but wait. */
	| 'interrupted'
	/** Not receiving and not trying any more. A reload is the only way back — when it ever had one to lose; see `recoverable` in `openDirectoryFeed`'s `onState`. */
	| 'unavailable'

interface DirectoryFeedHandlers {
	onState: (state: DirectoryFeedState, recoverable: boolean) => void
	onSignal: (signal: DirectorySignal) => void
	/**
	 * The session this feed was opened with has been revoked, and the server
	 * said so by closing the socket rather than by refusing a request. Nothing
	 * else here is terminal in that way: every other close leaves a person
	 * looking at a stale list, this one leaves them looking at a list they are
	 * no longer allowed to see.
	 */
	onRevoked: () => void
}

/**
 * What `openDirectoryFeed` hands back — nothing about the channel itself,
 * only the two things a caller may do to it. Named rather than left as an
 * inferred return, because `directory-feed-provider.tsx` holds one of these
 * in a ref across renders and needs a type to hold it at.
 */
export interface DirectoryFeedHandle {
	/** Tear the feed down. Idempotent-adjacent: called once, on unmount. */
	close(): void
	/**
	 * Call a directory RPC on the channel this feed opened, or do nothing at
	 * all — see the implementation for when and why.
	 */
	rpc(method: string, data: unknown): Promise<RpcResult> | undefined
}

/**
 * The close an administrator's force-disconnect produces, and the one reason
 * this module knows a code by number. It is `REALTIME_REVOKED_CLOSE` in
 * `backend/src/services/websocket/constants.ts`; Centrifugo passes a
 * server-side disconnect code through untouched, and 4500 is in the range it
 * does not reconnect after.
 */
const REVOKED_CLOSE_CODE = 4500

/**
 * Open the directory change feed and keep it open. Returns the handle that
 * closes it and the one other thing a caller may do on it — send an RPC —
 * gated on the same `live` this module already tracks for its own reason.
 *
 * **No React in here.** This is an imperative bridge to a library that owns
 * its own event loop, its own reconnection schedule and its own timers; the
 * component that mounts it owns nothing but the call and the cleanup. Keeping
 * the two apart is also what makes the state machine below readable as a state
 * machine instead of as a pile of effects.
 *
 * **Nothing about the channel is constructed here.** The channel name, the
 * socket URL and both tokens come from one call to
 * `/v1/notifications/directory-connection-tokens`, which derives the channel
 * from the session and takes no input. A second place that spelled
 * `directory:{orgId}.{userId}` would be a second place that could spell it
 * differently from the token the Sync Server signs, and the symptom of that is
 * a subscription that succeeds and then receives nothing forever.
 *
 * **Two tokens, two lifetimes, one endpoint.** Centrifugo checks the
 * connection token on the socket and the subscription token against its
 * `channel` claim, and expires them separately (`expired_close_delay: "10s"`).
 * Both are renewed through the `getToken` callbacks below, which is the whole
 * of the renewal story: a tab left open overnight renews instead of dying at
 * the first expiry.
 */
export function openDirectoryFeed({
	onState,
	onSignal,
	onRevoked
}: DirectoryFeedHandlers): DirectoryFeedHandle {
	let client: Centrifuge | undefined
	let closed = false
	// Whether the subscription has ever reached `live`. Read only when
	// reporting `unavailable`, to tell two different failures apart: one that
	// had a working connection and lost it (a reload has already worked once,
	// so it is fair to say it will again) from one that never connected at all
	// (a rejected token, a blocked socket — a reload hits the same wall).
	let hasBeenLive = false
	// Whether it is live *right now*, which `rpc` below reads instead of the
	// `DirectoryFeedState` the caller sees. That state reaches the caller
	// through a React state update, so a click a moment after the feed drops
	// could still read a stale `live` from the last render; this flag is
	// written synchronously by the same handlers that call `reportState`, so
	// `rpc` never sends on a channel Centrifugo has already stopped serving.
	let isLive = false

	// Every callback into the caller goes through these two. Centrifugo emits
	// a `disconnected` in response to our own `disconnect()`, so without the
	// guard closing the feed would report it as a failure on the way out, to a
	// component that is already unmounted.
	const reportState = (state: DirectoryFeedState, recoverable = false) => {
		if (!closed) {
			onState(state, recoverable)
		}
	}
	const reportSignal = (signal: DirectorySignal) => {
		if (!closed) {
			onSignal(signal)
		}
	}

	/**
	 * The two `getToken` callbacks fire within milliseconds of each other,
	 * because both tokens are minted with the same TTL from the same constant
	 * and therefore expire together. One request serves both.
	 *
	 * An in-flight share and NOT a cache: the promise is dropped as soon as it
	 * settles, so the next renewal fetches again. Holding the response would
	 * mean handing back a token that has already expired, which is the one
	 * thing this code exists to prevent.
	 */
	let inFlight: ReturnType<typeof fetchCredentials> | undefined

	function credentials() {
		inFlight ??= fetchCredentials().finally(() => {
			inFlight = undefined
		})
		return inFlight
	}

	/**
	 * A renewal, refused for good or failing for now (OPSSTD-7030).
	 *
	 * Centrifugo retries every failure of `getToken` with its own backoff,
	 * which is right for a 503 and wrong for a person taken out of the
	 * organization while the tab is open: the server says no every time, and
	 * the tab would ask every twenty seconds for as long as it stayed open.
	 * A refusal of access carries the `denied` category; that one becomes
	 * `UnauthorizedError`, which is what stops the client, and the feed
	 * reports itself unavailable. Everything else — a fault, a rate limit, the
	 * network — is rethrown as it came, and the backoff stays.
	 */
	async function renewed(token: 'subscriptionToken' | 'token') {
		try {
			return (await credentials())[token]
		} catch (error) {
			if (refusedForGood(error)) {
				throw new UnauthorizedError('directory-feed: token refused')
			}
			throw error
		}
	}

	async function start() {
		const { channel, wsUrl, token, subscriptionToken } = await credentials()

		// The caller unmounted while the first request was in flight. Opening
		// a socket now would leave one that nobody holds a closer for.
		if (closed) {
			return
		}

		client = new Centrifuge(resolveWsUrl(wsUrl), {
			token,
			getToken: () => renewed('token')
		})

		// `recoverable` is not requested even though recovery is the point of
		// this subscription: the `directory` namespace is configured
		// `force_recovery: true`, so the server makes every subscription in it
		// recoverable whether or not the client asks. Asking as well would put
		// a second declaration of a server-side policy in the browser, free to
		// drift from it and impossible to notice when it did.
		const subscription = client.newSubscription(channel, {
			token: subscriptionToken,
			getToken: () => renewed('subscriptionToken')
		})

		// `connectCalled` is the first attempt and the only one worth staying
		// quiet through. Every other code means an attempt already failed —
		// the transport closed, a ping never came — so from here on the person
		// is looking at a list that may have moved on without them.
		client.on('connecting', (ctx) => {
			isLive = false
			reportState(
				ctx.code === connectingCodes.connectCalled
					? 'connecting'
					: 'interrupted'
			)
		})

		// Centrifugo emits `disconnected` only when it has stopped trying: an
		// unauthorized connection, a protocol it cannot speak, or our own
		// `disconnect()`. Everything transient comes back as `connecting`
		// above. So this is terminal by construction, and the state says so
		// rather than implying a wait that will never end.
		client.on('disconnected', (ctx) => {
			// The code and the reason are the only thing that tells a refused
			// token apart from a socket the browser would not open, and both
			// look identical from the outside: the banner says the feed is
			// down either way. Centrifugo's own numbering (3500 unauthorized,
			// 3501 unknown channel, 3502 permission denied) is what names it,
			// and on a deployed environment the console is where somebody can
			// read it (OPSSTD-7953).
			isLive = false
			console.warn(
				`directory-feed: connection closed for good (code ${ctx.code}: ${ctx.reason})`
			)
			reportState('unavailable', hasBeenLive)

			// Reported after the state and not instead of it: the feed really
			// is unavailable, and whoever is showing that has no idea a
			// redirect is coming.
			if (ctx.code === REVOKED_CLOSE_CODE && !closed) {
				onRevoked()
			}
		})

		subscription.on('subscribing', (ctx) => {
			isLive = false
			reportState(
				ctx.code === subscribingCodes.subscribeCalled
					? 'connecting'
					: 'interrupted'
			)
		})

		// Connected is not live, which is why this and not the client's own
		// `connected` is where `live` is declared: the socket can be open
		// while the subscription is still being confirmed, and it is the
		// subscription that carries the events.
		subscription.on('subscribed', (ctx) => {
			hasBeenLive = true
			isLive = true
			reportState('live')

			if (leftAGap(ctx)) {
				reportSignal({ kind: 'desync' })
			}
		})

		// Same reasoning as `disconnected`: a subscription that is retrying
		// reports `subscribing`, so reaching `unsubscribed` without our having
		// asked means the server refused it for good.
		subscription.on('unsubscribed', (ctx) => {
			isLive = false
			// Same reason as the disconnect above: the subscription token is
			// signed separately from the connection one and can be refused on
			// its own, which from the outside looks like the same dead feed.
			console.warn(
				`directory-feed: subscription ended for good (code ${ctx.code}: ${ctx.reason})`
			)
			reportState('unavailable', hasBeenLive)
		})

		subscription.on('publication', (ctx) => {
			const signal = toDirectorySignal(ctx.data)

			if (signal) {
				reportSignal(signal)
			}
		})

		subscription.subscribe()
		client.connect()
	}

	// The first credentials request can fail — the session may have died in
	// the moment between the route guard and this call. There is no retry
	// here, on purpose: a 401 has already started the SDK's own session
	// recovery, which clears the cache and sends the person to the login page,
	// so this whole tree is about to unmount. Anything else leaves the feed
	// unavailable and says so, and that is the end of it — no polling loop
	// gets stood up as a consolation, because the one that gets stood up as a
	// consolation is the one that is still there in a year.
	start().catch(() => {
		reportState('unavailable', hasBeenLive)
	})

	return {
		close() {
			closed = true
			try {
				// One call, not two. `disconnect()` tears down every
				// subscription on the client with it, and the client is this
				// feed's alone — nothing else shares it, so there is nothing
				// left over to clean up.
				//
				// Guarded, because it can throw. Centrifugo's websocket
				// transport keeps its native `WebSocket` in a field that
				// starts `null` and only gets set once the socket is
				// actually constructed; a browser that refuses to open one
				// at all — an insecure `ws://` URL on an https page is
				// exactly this — leaves that field `null`, and
				// `disconnect()` calls `.close()` on it unconditionally.
				// There is nothing to recover: the feed has already reported
				// `unavailable`, and this is cleanup on the way out.
				client?.disconnect()
			} catch {
				// See above.
			}
		},

		/**
		 * Call a directory RPC on the channel this feed opened — the one
		 * other thing a caller may do with it besides listening and closing
		 * it.
		 *
		 * **`undefined` rather than a rejected promise when the feed is not
		 * `live`.** A caller with something to send when the feed drops has
		 * nothing to retry it with — there is no queue here, on purpose, for
		 * `announceNodeViewed`'s own reason: a view announced minutes late
		 * over a channel that just came back is not the view that happened,
		 * it is a guess standing in for one. `isLive` and not the
		 * `DirectoryFeedState` the caller was last given, because that state
		 * reaches them through a React render and a click a moment after a
		 * drop could still read the stale one; this flag is written by the
		 * same handlers that report the state, synchronously and before
		 * anything downstream re-renders.
		 */
		rpc(method: string, data: unknown) {
			if (!(isLive && client)) {
				return undefined
			}

			return client.rpc(method, data)
		}
	}
}

/**
 * A browser refuses to open a plain `ws://` socket from an `https:` page as
 * mixed content, so a `wsUrl` that has not kept up with the origin's own
 * protocol is a connection that can never succeed. Rewriting it here is the
 * one place this feed touches the URL rather than passing it through, and it
 * is safe: Centrifugo's websocket transport is symmetric on `ws`/`wss`, only
 * the scheme differs.
 */
let warnedInsecureWsUrl = false

function resolveWsUrl(wsUrl: string) {
	if (window.location.protocol === 'https:' && wsUrl.startsWith('ws://')) {
		if (!warnedInsecureWsUrl) {
			warnedInsecureWsUrl = true
			console.warn(
				'directory-feed: rewriting an insecure ws:// connection URL to wss:// on an https page'
			)
		}
		return `wss://${wsUrl.slice('ws://'.length)}`
	}

	return wsUrl
}

/**
 * Could something have been published that this subscription will never
 * deliver?
 *
 * On a resubscribe `wasRecovering` is true and `recovered` is the verdict:
 * true means Centrifugo replayed the gap and the missed publications are on
 * their way as ordinary events, false means the gap was wider than
 * `history_size: 200` or older than `history_ttl: 600s` and nothing will
 * replay it.
 *
 * **The first subscribe has a gap too** (OPSSTD-7217). The page read its lists
 * before this subscription existed — a token request, a connection and a
 * subscribe ago — and anything published in between is not replayed and not
 * counted as missed: `wasRecovering` is false because there was no position to
 * recover from, not because nothing happened. So the answer is «yes» unless a
 * recovery actually replayed the gap, and the price is one re-read of what is
 * on screen when a tab opens.
 */
function leftAGap(ctx: SubscribedContext) {
	return !(ctx.wasRecovering && ctx.recovered)
}

function refusedForGood(error: unknown) {
	return (
		typeof error === 'object' &&
		error !== null &&
		'category' in error &&
		error.category === 'denied'
	)
}

async function fetchCredentials() {
	const { data } = await v1NotificationsDirectoryConnectionToken()
	return data
}
